/**
 * Bundle Functionality for Shop by Look
 * Handles product selection, calculations, and cart operations
 */

class BundleFunctionality {
  constructor() {
    this.selectedProducts = new Map();
    this.productsInCart = new Set(); // Track which products are in cart
    this.isLoading = false;
    this.discountPercentage = 10; // Default discount
    
    // Get locale from bundleSettings or detect from HTML lang attribute
    this.locale = window.bundleSettings?.locale || document.documentElement.lang || 'es';
    
    // Get text settings from window.bundleSettings or use defaults
    this.texts = {
      productSelectedSingular: (window.bundleSettings?.productSelectedSingular || 'producto seleccionado:'),
      productsSelectedPlural: (window.bundleSettings?.productsSelectedPlural || 'productos seleccionados:'),
      quantityText: (window.bundleSettings?.quantityText || 'Cantidad:'),
      addProductErrorText: (window.bundleSettings?.addProductErrorText || 'Error al añadir el producto al carrito'),
      addBundleErrorText: (window.bundleSettings?.addBundleErrorText || 'Error al añadir el conjunto al carrito'),
      selectedItemsText: (window.bundleSettings?.selectedItemsText || 'productos seleccionados:'),
      viewCartButtonText: (window.bundleSettings?.viewCartButtonText || 'Ver carrito'),
      addIndividualButtonText: (window.bundleSettings?.addIndividualButtonText || 'Añadir al carrito'),
      addedToCartButtonText: (window.bundleSettings?.addedToCartButtonText || 'Agregado ✓'),
      preOrderButtonText: (window.bundleSettings?.preOrderButtonText || 'Compra anticipada')
    };
     
    this.init();
  }  
  init() { 
    this.initializeProductStates();
    this.bindEvents();
    this.setupScrollListener();
    this.initializeCustomSelects();
    this.initMobileImageSwipe();
    this.interceptQuickViewFromBundle();
    // Get discount percentage from button
    const bundleBtn = document.getElementById('bundle-add-all-btn');
    if (bundleBtn) {
      this.discountPercentage = parseInt(bundleBtn.dataset.discountPercentage) || 10;
    }
  }

  updateProductSku(productId, sku) {
    const productItem = document.querySelector(`.product-item[data-product-id="${productId}"]`);
    if (!productItem) return;

    const skuElement = productItem.querySelector('[data-product-sku]');
    if (!skuElement) return;

    const template = skuElement.dataset.skuTemplate || 'SKU: [[value]]';
    const globalSku = skuElement.dataset.globalSku || '';

    if (!sku) {
      if (globalSku) {
        skuElement.textContent = template.replace('[[value]]', globalSku);
        skuElement.setAttribute('data-showing-sku', 'true');
      } else {
        skuElement.textContent = '';
        skuElement.setAttribute('data-showing-sku', 'false');
      }
      return;
    }

    skuElement.textContent = template.replace('[[value]]', sku);
    skuElement.setAttribute('data-showing-sku', 'true');
  }

  initializeProductStates() {
    // Initialize all products with preselected variants
    const bundleItems = document.querySelectorAll('.product-item');
    
    bundleItems.forEach(item => {
      const productId = item.dataset.productId;
      const sizeSelector = item.querySelector('.bundle-size-selector');
      
      if (sizeSelector && sizeSelector.value) {
        // If there's a preselected value, enable the button and update price
        this.handleSizeChange(sizeSelector);
      }
    });
  }

  setupScrollListener() {
    // Simplified - no scroll listeners needed for fixed sticky position
    console.log('No scroll listeners needed with fixed sticky positioning');
  }

  initializeCustomSelects() {
    // Initialize all custom select dropdowns
    const customSelects = document.querySelectorAll('.bundle-custom-select');
    
    customSelects.forEach(select => {
      this.setupCustomSelect(select);
    });
  }

  setupCustomSelect(selectElement) {
    const trigger = selectElement.querySelector('.bundle-custom-select__trigger');
    const options = selectElement.querySelectorAll('.bundle-custom-select__option');
    const hiddenInput = selectElement.querySelector('input[type="hidden"]');
    const textElement = selectElement.querySelector('.bundle-custom-select__text');
    
    // Handle trigger click
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Close other open selects
      document.querySelectorAll('.bundle-custom-select.open').forEach(otherSelect => {
        if (otherSelect !== selectElement) {
          otherSelect.classList.remove('open');
        }
      });
      
      // Toggle this select
      selectElement.classList.toggle('open');
    });

    // Handle option selection
    options.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const value = option.dataset.value;
        const text = option.textContent.trim();
        
        // Update selected state
        options.forEach(opt => opt.classList.remove('selected'));
        option.classList.add('selected');
        
        // Update trigger display
        textElement.textContent = text;
        trigger.dataset.value = value;
        
        // Update hidden input
        hiddenInput.value = value;
        
        // Close dropdown
        selectElement.classList.remove('open');
        
        // Trigger change event on hidden input for compatibility
        const changeEvent = new Event('change', { bubbles: true });
        hiddenInput.dispatchEvent(changeEvent);
        
        // Handle size selector specific logic
        if (selectElement.classList.contains('bundle-custom-select') && !selectElement.classList.contains('bundle-custom-select--quantity')) {
          this.handleSizeChange(hiddenInput);
        }
        
        // Handle quantity selector specific logic
        if (selectElement.classList.contains('bundle-custom-select--quantity')) {
          this.handleQuantityChange(hiddenInput);
        }
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!selectElement.contains(e.target)) {
        selectElement.classList.remove('open');
      }
    });

    // Close dropdown on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        selectElement.classList.remove('open');
      }
    });
  }

  bindEvents() {
    // Size selector changes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('bundle-size-selector')) {
        this.handleSizeChange(e.target);
      }
    });

    // Quantity selector changes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('bundle-quantity-selector')) {
        this.handleQuantityChange(e.target);
      }
    });

    // Size button clicks (visual grid)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.bundle-size-option');
      if (!btn) return;
      e.preventDefault();
      const productId = btn.dataset.productId;
      const variantId = btn.dataset.variantId;
      const hiddenInput = document.querySelector(`.bundle-size-selector[data-product-id="${productId}"]`);
      
      if (hiddenInput) {
        // Check if clicking on already selected size (to deselect)
        const isAlreadySelected = btn.classList.contains('is-selected');
        
        if (isAlreadySelected) {
          // Don't allow deselection if product is already in cart
          if (this.productsInCart.has(productId)) {
            return; // Exit early - no deselection allowed
          }
          
          // Deselect: clear value and remove from bundle
          hiddenInput.value = '';
          this.setSelectedSizeButton(productId, null);
          this.removeFromSelectedProducts(productId);
          this.hideBundleLabel(productId);
          this.hidePreOrderLabel(productId);
          this.disableIndividualButton(productId);
          this.updateProductSku(productId, '');
          this.updateUI();
        } else {
          // Select: set value and add to bundle
          hiddenInput.value = variantId;
          this.setSelectedSizeButton(productId, variantId);
          this.handleSizeChange(hiddenInput);
        }
      }
    });

    // Individual add to cart buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.bundle-add-individual')) {
        e.preventDefault();
        this.handleIndividualAddToCart(e.target.closest('.bundle-add-individual'));
      }
    });

    // Product selection checkboxes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('bundle-product-checkbox')) {
        this.handleProductSelection(e.target);
      }
    });

    // Bundle add all button
    const bundleAddAllBtn = document.getElementById('bundle-add-all-btn');
    if (bundleAddAllBtn) {
      bundleAddAllBtn.addEventListener('click', () => {
        this.handleBundleAddToCart();
      });
    }

    // Sticky bundle button
    const stickyBundleBtn = document.getElementById('bundle-sticky-add-btn');
    if (stickyBundleBtn) {
      stickyBundleBtn.addEventListener('click', () => {
        this.handleBundleAddToCart();
      });
    }

    // Sticky close button
    const stickyCloseBtn = document.getElementById('bundle-sticky-close');
    if (stickyCloseBtn) {
      stickyCloseBtn.addEventListener('click', () => {
        this.clearSelections();
      });
    }

    // Bundle toggle button
    const toggleBtn = document.getElementById('bundle-toggle-btn');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        this.handleBundleToggle();
      });
    }

    // Close mobile calculator when clicking outside
    document.addEventListener('click', (e) => {
      const calculator = document.getElementById('bundle-calculator');
      const toggle = document.getElementById('bundle-calculator-toggle');
      
      if (calculator && calculator.classList.contains('bundle-calculator--visible')) {
        if (!calculator.contains(e.target) && !toggle.contains(e.target)) {
          this.hideMobileCalculator();
        }
      }
    });
  }

  handleSizeChange(sizeSelector) {
    const productId = sizeSelector.dataset.productId;
    const variantId = sizeSelector.value;

    if (!variantId) {
      this.disableIndividualButton(productId);
      // Remove from bundle when no size selected
      this.removeFromSelectedProducts(productId);
      this.hideBundleLabel(productId);
      this.updateUI();
      this.updateProductSku(productId, "");
      return;
    }

    // Get price data from the selected option in the custom dropdown
    const customSelect = sizeSelector.closest('.bundle-custom-select') || sizeSelector.parentElement.querySelector('.bundle-custom-select');
    const selectedOption = customSelect ? customSelect.querySelector('.bundle-custom-select__option.selected') : null;
    
    let price = 0;
    let comparePrice = 0;
    let isPreOrder = false;
    let sku = "";

    if (selectedOption) {
      price = parseFloat(selectedOption.dataset.price) || 0;
      comparePrice = parseFloat(selectedOption.dataset.comparePrice) || 0;
      isPreOrder = (selectedOption.dataset.preOrder || '').toLowerCase() === 'true';
      sku = selectedOption.dataset.sku || "";
    } else {
      // Fallback: try to find the option by variant ID
      const allOptions = document.querySelectorAll(`[data-product-id="${productId}"] .bundle-custom-select__option[data-value="${variantId}"]`);
      if (allOptions.length > 0) {
        price = parseFloat(allOptions[0].dataset.price) || 0;
        comparePrice = parseFloat(allOptions[0].dataset.comparePrice) || 0;
        isPreOrder = (allOptions[0].dataset.preOrder || '').toLowerCase() === 'true';
        sku = allOptions[0].dataset.sku || "";
      }
    }

    if (!sku && variantId) {
      const sizeButton = document.querySelector(`.bundle-size-option[data-product-id="${productId}"][data-variant-id="${variantId}"]`);
      if (sizeButton) {
        sku = sizeButton.dataset.sku || "";
      }
    }

    // Update price display
    this.updateProductPrice(productId, price, comparePrice);
    
    // Enable individual add to cart button
    this.enableIndividualButton(productId);
    
    // Handle pre-order state
    if (isPreOrder) {
      this.showPreOrderLabel(productId);
      this.updateButtonTextForPreOrder(productId, true);
    } else {
      this.hidePreOrderLabel(productId);
      this.updateButtonTextForPreOrder(productId, false);
    }
    
    // Reset button text to normal when changing size
    const button = document.querySelector(`.bundle-add-individual[data-product-id="${productId}"]`);
    if (button && this.productsInCart.has(productId)) {
      // If product was in cart, remove it from cart tracking and reset UI
      this.productsInCart.delete(productId);
      this.hideBundleLabel(productId);
      this.updateIndividualButtonText(button, 'normal');
      this.updateBundleButtonState();
    }
    
    // Reflect selection on visual buttons
    this.setSelectedSizeButton(productId, variantId);

    // Automatically add to bundle when size is selected
    this.addToSelectedProducts(productId);
    // Don't show label yet - only show when added to cart
    this.updateUI();
    this.updateProductSku(productId, sku);
  }

  handleQuantityChange(quantitySelector) {
    const productId = quantitySelector.dataset.productId;
    
    // Update selected product data if it's in the bundle
    if (this.selectedProducts.has(productId)) {
      this.updateSelectedProduct(productId);
    }
  }

  handleProductSelection(checkbox) {
    const productId = checkbox.dataset.productId;
    
    if (checkbox.checked) {
      this.addToSelectedProducts(productId);
      this.showBundleLabel(productId);
    } else {
      this.removeFromSelectedProducts(productId);
      this.hideBundleLabel(productId);
    }
    
    this.updateUI();
  }

  addToSelectedProducts(productId) {
    const productData = this.getProductData(productId);
    if (productData) {
      this.selectedProducts.set(productId, productData);
    }
  }

  removeFromSelectedProducts(productId) {
    this.selectedProducts.delete(productId);
  }

  updateSelectedProduct(productId) {
    if (this.selectedProducts.has(productId)) {
      const productData = this.getProductData(productId);
      if (productData) {
        this.selectedProducts.set(productId, productData);
        this.updateUI();
      }
    }
  }

  // Visually mark the selected size button for a product
  setSelectedSizeButton(productId, variantId) {
    const container = document.querySelector(`.bundle-size-options[data-product-id="${productId}"]`);
    if (!container) return;
    const buttons = container.querySelectorAll('.bundle-size-option');
    buttons.forEach((btn) => {
      // If variantId is null, deselect all buttons
      const isSelected = variantId ? btn.dataset.variantId === String(variantId) : false;
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  getProductData(productId) {
    const productItem = document.querySelector(`[data-product-id="${productId}"]`);
    if (!productItem) return null;

    const sizeSelector = productItem.querySelector('.bundle-size-selector');
    const quantitySelector = productItem.querySelector('.bundle-quantity-selector');
    const productTitle = productItem.querySelector('.product-item__product-title a');
    
    const variantId = sizeSelector ? sizeSelector.value : null;
    const quantity = quantitySelector ? parseInt(quantitySelector.value) : 1;
    
    if (!variantId && sizeSelector) {
      return null; // Size not selected
    }

    // Get price data from the selected option in the custom dropdown
    const customSelect = productItem.querySelector('.bundle-custom-select:not(.bundle-custom-select--quantity)');
    const selectedOption = customSelect ? customSelect.querySelector('.bundle-custom-select__option.selected') : null;
    
    let price = 0;
    let comparePrice = 0;
    let variantText = '';
    
    if (selectedOption) {
      price = parseFloat(selectedOption.dataset.price) || 0;
      comparePrice = parseFloat(selectedOption.dataset.comparePrice) || 0;
      variantText = selectedOption.textContent.trim();
    } else if (variantId) {
      // Fallback: try to find the option by variant ID
      const allOptions = document.querySelectorAll(`[data-product-id="${productId}"] .bundle-custom-select__option[data-value="${variantId}"]`);
      if (allOptions.length > 0) {
        price = parseFloat(allOptions[0].dataset.price) || 0;
        comparePrice = parseFloat(allOptions[0].dataset.comparePrice) || 0;
        variantText = allOptions[0].textContent.trim();
      }
    }

    // Check if variant is pre-order (more robust check for true/false/"true"/"false")
    let isPreOrder = false;
    const checkPreOrderValue = (value) => {
      if (typeof value === 'string') value = value.toLowerCase();
      return value === true || value === 'true' || value === '1';
    };
    
    if (selectedOption) {
      isPreOrder = checkPreOrderValue(selectedOption.dataset.preOrder);
    } else if (variantId) {
      const allOptions = document.querySelectorAll(`[data-product-id="${productId}"] .bundle-custom-select__option[data-value="${variantId}"]`);
      if (allOptions.length > 0) {
        isPreOrder = checkPreOrderValue(allOptions[0].dataset.preOrder);
      }
    }

    // Also check the visual button for pre-order status
    if (!isPreOrder && variantId) {
      const sizeButton = document.querySelector(`.bundle-size-option[data-product-id="${productId}"][data-variant-id="${variantId}"]`);
      if (sizeButton) {
        isPreOrder = checkPreOrderValue(sizeButton.dataset.preOrder);
      }
    }

    return {
      productId,
      variantId: variantId || productId, // Fallback for products without variants
      title: productTitle ? productTitle.textContent.trim() : 'Producto',
      variantText,
      price,
      comparePrice,
      quantity,
      totalPrice: price * quantity,
      isPreOrder
    };
  }

  updateProductPrice(productId, price, comparePrice) {
    const priceElement = document.querySelector(`[data-product-id="${productId}"] .bundle-price`);
    if (!priceElement) return;

    const currentPriceEl = priceElement.querySelector('.bundle-price__current');
    const comparePriceEl = priceElement.querySelector('.bundle-price__compare');

    if (currentPriceEl) {
      currentPriceEl.textContent = this.formatMoney(price);
    }

    if (comparePriceEl) {
      if (comparePrice > price) {
        comparePriceEl.textContent = this.formatMoney(comparePrice);
        comparePriceEl.style.display = 'inline';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }
  }

  enableIndividualButton(productId) {
    const button = document.querySelector(`[data-product-id="${productId}"] .bundle-add-individual`);
    if (button) {
      button.disabled = false;
    }
  }

  disableIndividualButton(productId) {
    const button = document.querySelector(`[data-product-id="${productId}"] .bundle-add-individual`);
    if (button) {
      button.disabled = true;
    }
  }

  async handleIndividualAddToCart(button) {
    if (this.isLoading) return;
    
    const productId = button.dataset.productId;
    const productData = this.getProductData(productId);
    
    if (!productData || !productData.variantId) {
      this.showError('Por favor selecciona una talla');
      return;
    }

    this.setButtonLoading(button, true);
    
    try {
      // Prepare cart item with pre-order properties if needed
      const cartItem = {
        id: productData.variantId,
        quantity: productData.quantity
      };
      
      // Add Globo pre-order properties if variant is pre-order
      if (productData.isPreOrder) {
        cartItem.properties = {
          '_is_preorder': 'Pre-Order',
          '_preorder_locale': this.locale,
          '_esid': this.generateUniqueId()
        };
      }
      
      await this.addToCart([cartItem]);
      
      // Mark product as in cart and show label
      this.productsInCart.add(productId);
      this.showBundleLabel(productId);
      
      // Change button text to "Added"
      this.updateIndividualButtonText(button, 'added');
      
      // Update bundle button to "View Cart" if any products are in cart
      this.updateBundleButtonState();
      
    } catch (error) {
      this.showError(this.texts.addProductErrorText);
      console.error('Add to cart error:', error);
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  async handleBundleAddToCart() {
    if (this.isLoading || this.selectedProducts.size === 0) return;

    const bundleBtn = document.getElementById('bundle-add-all-btn');
    this.setButtonLoading(bundleBtn, true);

    try {
      // Prepare items with pre-order properties if needed
      const items = Array.from(this.selectedProducts.values()).map(product => {
        const cartItem = {
          id: product.variantId,
          quantity: product.quantity
        };
        
        // Add Globo pre-order properties if variant is pre-order
        if (product.isPreOrder) {
          cartItem.properties = {
            '_is_preorder': 'Pre-Order',
            '_preorder_locale': this.locale,
            '_esid': this.generateUniqueId()
          };
        }
        
        return cartItem;
      });

      await this.addToCart(items);
      
      // Mark all products as in cart and show labels
      this.selectedProducts.forEach((product, productId) => {
        this.productsInCart.add(productId);
        this.showBundleLabel(productId);
      });
      
      // Update bundle button to "View Cart"
      this.updateBundleButtonState();
      
      // Keep selections after adding to cart - don't clear
      // this.clearSelections();
      
    } catch (error) {
      this.showError(this.texts.addBundleErrorText);
      console.error('Bundle add to cart error:', error);
    } finally {
      this.setButtonLoading(bundleBtn, false);
    }
  }

  async addToCart(items) {    
    // Always try native theme functions first (now supports properties)
    const cartObj = window.themeCart;
    
    if (cartObj && cartObj.addItemById) {
      return this.addToCartWithNative(items, cartObj);
    } else {
      // Fallback to fetch if native cart not available
      return this.addToCartWithFetch(items);
    }
  }

  async addToCartWithNative(items, cartObj) {
    const results = [];
    
    for (const item of items) {
      try {
        // Pass properties if they exist (for pre-order support)
        const result = await cartObj.addItemById(item.id, item.quantity, item.properties || {});
        results.push(result);
      } catch (error) {
        throw error;
      }
    }

    // Check if purchase confirmation is enabled
    const purchaseConfirmation = document.querySelector('[data-section-type="confirmation-popup"]');
    
    if (purchaseConfirmation && window.r$1) {
      // Show confirmation popup for the first item added
      window.r$1("confirmation-popup:open", null, { 
        product: results[0] 
      });
    } else if (window.r$1) {
      // Open minicart directly
      window.r$1("quick-cart:updated");
      setTimeout(() => {
        window.r$1("quick-cart:open");
      }, 300);
    }
    
    return results;
  }

  async addToCartWithFetch(items) {
    
    // Trigger cart:updating event like native theme
    if (window.r$1) {
      window.r$1("cart:updating");
    }
    
    const data = {
      items: items.map(item => {
        const cartItem = {
          id: item.id,
          quantity: item.quantity
        };
        
        // Include properties if they exist (for pre-order support)
        if (item.properties) {
          cartItem.properties = item.properties;
        }
        
        return cartItem;
      })
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.status == "422") {
      throw new Error(result.description || 'Error adding items to cart');
    }
    
    // Get fresh cart data and trigger events like native theme
    const cartResponse = await fetch('/cart.js', {
      method: 'GET',
      credentials: 'include'
    });
    
    const cartData = await cartResponse.json();
    
    // Trigger events in same order as native theme
    if (window.r$1) {
      window.r$1("quick-cart:updated");
      window.r$1("cart:updated", { cart: cartData });
    }
    
    // Show confirmation popup or open minicart (same logic as native method)
    const purchaseConfirmation = document.querySelector('[data-section-type="confirmation-popup"]');
    
    if (purchaseConfirmation && window.r$1) {
      // Show confirmation popup for the first item added
      window.r$1("confirmation-popup:open", null, { 
        product: result.items ? result.items[0] : result 
      });
    } else if (window.r$1) {
      // Open minicart directly
      setTimeout(() => {
        window.r$1("quick-cart:open");
      }, 300);
    }
    
    return result;
  }

  // Generate unique ID for Globo pre-order (UUID v4 format)
  generateUniqueId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  clearSelections() {
    // Uncheck all checkboxes
    document.querySelectorAll('.bundle-product-checkbox:checked').forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Clear selected products
    this.selectedProducts.clear();
    
    // Update UI
    this.updateUI();
  }

  updateUI() {
    this.updateBundleSummary();
    this.updateBundlePricing();
    this.updateBundleButton();
    this.updateMobileToggle();
    this.updateStickySummary();
  }

  updateBundleSummary() {
    const countElement = document.getElementById('bundle-count');
    const itemsContainer = document.getElementById('bundle-selected-items');
    
    if (!countElement || !itemsContainer) return;

    const count = this.selectedProducts.size;
    countElement.textContent = `${count} ${count === 1 ? this.texts.productSelectedSingular : this.texts.productsSelectedPlural}`;

    // Clear and rebuild items list
    itemsContainer.innerHTML = '';
    
    this.selectedProducts.forEach(product => {
      const itemElement = document.createElement('div');
      itemElement.className = 'bundle-selected-item';
      itemElement.innerHTML = `
        <div class="bundle-selected-item__info">
          <div class="bundle-selected-item__name">${product.title}</div>
          <div class="bundle-selected-item__details">
            ${product.variantText ? product.variantText + ' • ' : ''}${this.texts.quantityText} ${product.quantity}
          </div>
        </div>
        <div class="bundle-selected-item__price">${this.formatMoney(product.totalPrice)}</div>
      `;
      itemsContainer.appendChild(itemElement);
    });
  }

  updateBundlePricing() {
    const subtotalElement = document.getElementById('bundle-subtotal');
    const discountElement = document.getElementById('bundle-discount');
    const discountRow = document.getElementById('bundle-discount-row');
    const totalElement = document.getElementById('bundle-total');
    
    if (!subtotalElement || !totalElement) return;

    const subtotal = Array.from(this.selectedProducts.values())
      .reduce((sum, product) => sum + product.totalPrice, 0);
    
    // Check if bundle savings are enabled
    const bundleCalculator = document.getElementById('bundle-calculator');
    const showBundleSavings = bundleCalculator && bundleCalculator.dataset.showSavings === 'true';
    
    let discount = 0;
    let total = subtotal;
    
    // Only apply discount if savings are enabled and there are multiple products
    if (showBundleSavings && this.selectedProducts.size > 1 && this.discountPercentage > 0) {
      discount = subtotal * (this.discountPercentage / 100);
      total = subtotal - discount;
    }

    subtotalElement.textContent = this.formatMoney(subtotal);
    totalElement.textContent = this.formatMoney(total);

    if (discountElement && discountRow) {
      if (showBundleSavings && this.selectedProducts.size > 1 && discount > 0) {
        discountElement.textContent = `-${this.formatMoney(discount)}`;
        discountRow.style.display = 'flex';
      } else {
        discountRow.style.display = 'none';
      }
    }
  }

  updateBundleButton() {
    const bundleButton = document.getElementById('bundle-add-all-btn');
    if (!bundleButton) return;

    const hasValidSelections = Array.from(this.selectedProducts.values())
      .every(product => product.variantId);

    bundleButton.disabled = this.selectedProducts.size === 0 || !hasValidSelections;
  }

  updateBundleButtonState() {
    const bundleButton = document.getElementById('bundle-add-all-btn');
    const stickyButton = document.getElementById('bundle-sticky-add-btn');
    
    // Check if any products are in cart
    const hasProductsInCart = this.productsInCart.size > 0;
    
    if (hasProductsInCart) {
      // Update main bundle button (if exists)
      if (bundleButton) {
        const buttonText = bundleButton.querySelector('.btn__text');
        if (buttonText) {
          buttonText.textContent = this.texts.viewCartButtonText;
        }
        
        // Remove old event listeners by cloning
        const newBundleButton = bundleButton.cloneNode(true);
        bundleButton.parentNode.replaceChild(newBundleButton, bundleButton);
        
        // Add new click handler
        newBundleButton.addEventListener('click', (e) => {
          e.preventDefault();
          window.location.href = '/cart';
        });
        newBundleButton.disabled = false;
      }
      
      // Update sticky button (if exists)
      if (stickyButton) {
        const stickyText = stickyButton.querySelector('.btn__text');
        if (stickyText) {
          stickyText.textContent = this.texts.viewCartButtonText;
        }
        
        // Remove old event listeners by cloning
        const newStickyButton = stickyButton.cloneNode(true);
        stickyButton.parentNode.replaceChild(newStickyButton, stickyButton);
        
        // Add new click handler
        newStickyButton.addEventListener('click', (e) => {
          e.preventDefault();
          window.location.href = '/cart';
        });
        newStickyButton.disabled = false;
      }
    }
  }

  // Helper method to check if button should be in "View Cart" mode
  isInViewCartMode() {
    return this.productsInCart.size > 0;
  }

  updateMobileToggle() {
    const toggleCount = document.getElementById('bundle-toggle-count');
    const toggleButton = document.getElementById('bundle-calculator-toggle');
    const toggleTotal = document.getElementById('bundle-toggle-total');
    const total = Array.from(this.selectedProducts.values())
      .reduce((sum, product) => sum + product.totalPrice, 0);
    
    if (toggleCount) {
      toggleCount.textContent = this.selectedProducts.size;
    }
    
    if (toggleTotal) {
      toggleTotal.textContent = this.formatMoney(total);
    }
    
    if (toggleButton) {
      if (this.selectedProducts.size > 0) {
        toggleButton.style.display = 'block';
      } else {
        toggleButton.style.display = 'none';
        this.hideMobileCalculator();
      }
    }
  }

  toggleMobileCalculator() {
    const calculator = document.getElementById('bundle-calculator');
    if (!calculator) return;

    calculator.classList.toggle('bundle-calculator--visible');
  }

  hideMobileCalculator() {
    const calculator = document.getElementById('bundle-calculator');
    if (calculator) {
      calculator.classList.remove('bundle-calculator--visible');
    }
  }

  setButtonLoading(button, loading) {
    if (!button) return;
    
    this.isLoading = loading;
    
    if (loading) {
      button.disabled = true;
      button.classList.add('btn--loading');
    } else {
      button.disabled = false;
      button.classList.remove('btn--loading');
    }
  }

  formatMoney(cents) {
    // Simple money formatting - adjust based on your shop's currency settings
    const amount = cents / 100;
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      // Hide decimals when they are zero (e.g. 128,00 € -> 128 €)
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  showSuccess(message) {
    
    // Simple alert for now - replace with your notification system
    if (window.theme && window.theme.showNotification) {
      window.theme.showNotification(message, 'success');
    } else {
      alert(message);
    }
  }

  showError(message) {
    
    // Simple alert for now - replace with your notification system
    if (window.theme && window.theme.showNotification) {
      window.theme.showNotification(message, 'error');
    } else {
      alert(message);
    }
  }

  showBundleLabel(productId) {
    const label = document.querySelector(`.bundle-selected-label[data-product-id="${productId}"]`);
    if (label) {
      label.style.display = 'block';
    }
  }

  hideBundleLabel(productId) {
    const label = document.querySelector(`.bundle-selected-label[data-product-id="${productId}"]`);
    if (label) {
      label.style.display = 'none';
    }
  }

  showPreOrderLabel(productId) {
    const label = document.querySelector(`.bundle-pre-order-label[data-product-id="${productId}"]`);
    if (label) {
      label.style.display = 'flex';
    }
  }

  hidePreOrderLabel(productId) {
    const label = document.querySelector(`.bundle-pre-order-label[data-product-id="${productId}"]`);
    if (label) {
      label.style.display = 'none';
    }
  }

  updateButtonTextForPreOrder(productId, isPreOrder) {
    const button = document.querySelector(`.bundle-add-individual[data-product-id="${productId}"]`);
    if (!button) return;
    
    const buttonText = button.querySelector('.btn__text');
    if (!buttonText) return;
    
    // Store pre-order state on button for later use
    button.dataset.isPreOrder = isPreOrder;
    
    if (isPreOrder) {
      buttonText.textContent = this.texts.preOrderButtonText;
    } else {
      buttonText.textContent = this.texts.addIndividualButtonText;
    }
  }

  updateIndividualButtonText(button, state) {
    if (!button) return;
    
    const buttonText = button.querySelector('.btn__text');
    if (!buttonText) return;
    
    // Check if this is a pre-order product
    const isPreOrder = button.dataset.isPreOrder === 'true';
    
    if (state === 'added') {
      // Change to "Added" state
      buttonText.textContent = this.texts.addedToCartButtonText;
      button.classList.add('bundle-add-individual--added');
    } else {
      // Reset to normal or pre-order state
      buttonText.textContent = isPreOrder ? this.texts.preOrderButtonText : this.texts.addIndividualButtonText;
      button.classList.remove('bundle-add-individual--added');
    }
  }

  updateStickySummary() {
    const stickySummary = document.getElementById('bundle-sticky-summary');
    const stickyCount = document.getElementById('bundle-sticky-count');
    const stickyItems = document.getElementById('bundle-sticky-items');
    const stickyTotal = document.getElementById('bundle-sticky-total');
    const stickyBtn = document.getElementById('bundle-sticky-add-btn');
    const stickySubtotal = document.getElementById('bundle-sticky-subtotal-value');
    const stickyDiscount = document.getElementById('bundle-sticky-discount-value');
    const stickySubtotalRow = document.getElementById('bundle-sticky-subtotal');
    const stickyDiscountRow = document.getElementById('bundle-sticky-discount');

    if (!stickySummary) return;

    const count = this.selectedProducts.size;

    // Update count
    if (stickyCount) {
      stickyCount.textContent = `${count} ${this.texts.selectedItemsText}`;
    }

    // Update items preview
    if (stickyItems) {
      stickyItems.innerHTML = '';
      this.selectedProducts.forEach(product => {
        const productElement = document.querySelector(`[data-product-id="${product.productId}"]`);
        const productImage = productElement?.querySelector('img');
        
        if (productImage) {
          const itemElement = document.createElement('div');
          itemElement.className = 'bundle-sticky-item';
          itemElement.innerHTML = `
            <img src="${productImage.src}" alt="${product.title}">
            ${product.quantity > 1 ? `<div class="bundle-sticky-item__badge">${product.quantity}</div>` : ''}
          `;
          stickyItems.appendChild(itemElement);
        }
      });
    }

    // Calculate pricing
    const subtotal = Array.from(this.selectedProducts.values())
      .reduce((sum, product) => sum + product.totalPrice, 0);
    
    const bundleCalculator = document.getElementById('bundle-calculator');
    const showBundleSavings = bundleCalculator && bundleCalculator.dataset.showSavings === 'true';
    
    let discount = 0;
    let total = subtotal;
    
    if (showBundleSavings && this.selectedProducts.size > 1 && this.discountPercentage > 0) {
      discount = subtotal * (this.discountPercentage / 100);
      total = subtotal - discount;
    }

    // Update pricing display
    if (stickySubtotal) {
      stickySubtotal.textContent = this.formatMoney(subtotal);
    }
    
    if (stickyDiscount) {
      stickyDiscount.textContent = `-${this.formatMoney(discount)}`;
    }
    
    if (stickyTotal) {
      stickyTotal.textContent = this.formatMoney(total);
    }

    // Show/hide discount rows
    if (stickySubtotalRow && stickyDiscountRow) {
      if (showBundleSavings && discount > 0) {
        stickySubtotalRow.style.display = 'flex';
        stickyDiscountRow.style.display = 'flex';
      } else {
        stickySubtotalRow.style.display = 'none';
        stickyDiscountRow.style.display = 'none';
      }
    }

    // Update button state
    if (stickyBtn) {
      const hasValidSelections = Array.from(this.selectedProducts.values())
        .every(product => product.variantId);
      stickyBtn.disabled = count === 0 || !hasValidSelections;
    }
  }

  handleBundleToggle() {
    // Check if we're on desktop or mobile
    const isDesktop = window.innerWidth >= 768;
    
    if (isDesktop) {
      // Desktop: Scroll to bundle calculator with 50px offset
      const bundleCalculator = document.getElementById('bundle-calculator');
      if (bundleCalculator) {
        const rect = bundleCalculator.getBoundingClientRect();
        const offsetTop = window.pageYOffset + rect.top;
        const targetPosition = offsetTop - window.innerHeight + bundleCalculator.offsetHeight + 50;
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      // Mobile: TODO - implement mobile behavior later
      console.log('Mobile bundle toggle - to be implemented');
    }
  }

  initMobileImageSwipe() {
    const productItems = document.querySelectorAll('.product-item--bundle .product-item__media--multiple-images');
    
    productItems.forEach(mediaContainer => {
      const imageLink = mediaContainer.querySelector('.product-item__image-link');
      const images = mediaContainer.querySelectorAll('.product-item__image');
      const navButtons = mediaContainer.querySelectorAll('.product-item__image-nav');
      const counter = mediaContainer.querySelector('.product-item__image-counter');
      const totalImages = images.length;
      
      if (!imageLink || totalImages === 0) return;

      let currentImageIndex = 0;
      let touchStartX = 0;
      let touchStartY = 0;
      let touchEndX = 0;
      let touchEndY = 0;
      let isSwiping = false;

      // Set initial state
      images.forEach((img, index) => {
        img.style.opacity = index === 0 ? '1' : '0';
      });

      // Update counter
      const updateCounter = () => {
        if (counter) {
          const currentSpan = counter.querySelector('.current');
          if (currentSpan) {
            currentSpan.textContent = currentImageIndex + 1;
          }
        }
      };

      // Navigate to specific image
      const showImage = (index) => {
        images.forEach((img, i) => {
          img.style.opacity = i === index ? '1' : '0';
        });
        currentImageIndex = index;
        updateCounter();
      };

      // Navigate next/prev
      const navigateImage = (direction) => {
        let newIndex = currentImageIndex + direction;
        
        // Loop around
        if (newIndex >= totalImages) {
          newIndex = 0;
        } else if (newIndex < 0) {
          newIndex = totalImages - 1;
        }
        
        showImage(newIndex);
      };

      // Navigation button clicks
      navButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const direction = btn.dataset.direction === 'next' ? 1 : -1;
          navigateImage(direction);
        });
      });

      // Touch events for swipe (mobile only)
      if (window.innerWidth <= 767) {
        imageLink.addEventListener('touchstart', (e) => {
          touchStartX = e.changedTouches[0].screenX;
          touchStartY = e.changedTouches[0].screenY;
          isSwiping = false;
        }, { passive: true });

        imageLink.addEventListener('touchmove', (e) => {
          touchEndX = e.changedTouches[0].screenX;
          touchEndY = e.changedTouches[0].screenY;
          
          const deltaX = Math.abs(touchEndX - touchStartX);
          const deltaY = Math.abs(touchEndY - touchStartY);
          
          // If horizontal movement is greater than vertical, it's a swipe
          if (deltaX > 15 && deltaX > deltaY) {
            isSwiping = true;
          }
        }, { passive: true });

        imageLink.addEventListener('touchend', (e) => {
          if (!isSwiping) {
            // It's a tap, let the link work normally
            return;
          }

          // It's a swipe, prevent navigation and navigate image
          e.preventDefault();
          e.stopPropagation();

          const swipeDistance = touchEndX - touchStartX;
          
          // Swipe threshold: 30px
          if (Math.abs(swipeDistance) > 30) {
            const direction = swipeDistance > 0 ? -1 : 1; // Swipe right = prev, left = next
            navigateImage(direction);
          }

          isSwiping = false;
          touchStartX = 0;
          touchEndX = 0;
        });
      }
    });
  }

  interceptQuickViewFromBundle() {
    let isFromBundle = false;

    // Intercept clicks on bundle quick-view buttons
    document.addEventListener('click', (e) => {
      const quickViewBtn = e.target.closest('[data-quick-shop-trigger="quick-view"]');
      
      if (quickViewBtn) {
        isFromBundle = quickViewBtn.dataset.source === 'bundle';
      }
    }, true);

    // Watch for quick-view modal content changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const quickProduct = node.querySelector ? node.querySelector('.quick-product') : null;
            const quickProductDirect = node.classList && node.classList.contains('quick-product') ? node : null;
            const targetElement = quickProduct || quickProductDirect;
            
            if (targetElement && isFromBundle) {
              targetElement.classList.add('quick-product--from-bundle');
              isFromBundle = false;
            }
          }
        });
      });
    });

    // Observe the modal container
    const modalContainer = document.querySelector('[data-quick-view-modal]');
    if (modalContainer) {
      observer.observe(modalContainer, {
        childList: true,
        subtree: true
      });
    }
  }
}

// Initialize the bundle functionality when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  window.bundleManager = new BundleFunctionality();
});
